import { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import { AssetGroupAvailability } from '../api/types';
import { getAvailabilityCount, getAvailableDurations } from './index';
import { STATION_TIME_ZONE, atStation, stationDateTime, stationTimeOfDay } from './stationTime';

// Availability payloads key days as DD.MM.YYYY and start times as HH:mm.
export const AVAILABILITY_DATE_KEY_FORMAT = 'DD.MM.YYYY';

export interface DurationOption {
    value: number;
    label: string;
}

// Durations beyond this are the storage "n months" options, which the
// skip-ending-in-days rule deliberately does not constrain.
const TWO_MONTHS_IN_MINUTES = 60 * 24 * 60;
const TWELVE_MONTHS_IN_MINUTES = 525601;
const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const unitLabel = (count: number, singular: string, plural: string, t: TFunction): string =>
    `${count} ${t(`booking_details.reservation_summary.${count === 1 ? singular : plural}`)}`;

// Duration label across the whole range the payloads produce: minutes (SUP
// slots), hours, days (storage) and the 12-months special case.
//
// A remainder is spelled out rather than divided into a decimal. The division
// was written for the tariff durations, which are whole multiples — fed
// anything else it produced arithmetic instead of a label: a 100-minute window
// read `1.6666666666666667 Stunden`, and it reached the screen through
// `withCurrentOption`, which offers the booking's OWN duration as an option.
// Storage bookings with exactly that shape exist (#2675, #2676).
//
// The same change makes SUP's 90-minute slot read "1 Stunde 30 Minuten" instead
// of "1.5 Stunden" — the decimal was never wrong there, but one rule that reads
// correctly everywhere beats two that disagree at the edges.
export const formatDurationLabel = (minutes: number, t: TFunction): string => {
    if (!Number.isFinite(minutes) || minutes < 0) return '';
    if (minutes === TWELVE_MONTHS_IN_MINUTES) {
        return t('create_booking.reservation_details.duration_12_months');
    }
    if (minutes < 60) {
        return unitLabel(minutes, 'minute', 'minutes', t);
    }
    if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        const restMinutes = minutes % 60;
        const hourPart = unitLabel(hours, 'hour', 'hours', t);
        return restMinutes === 0
            ? hourPart
            : `${hourPart} ${unitLabel(restMinutes, 'minute', 'minutes', t)}`;
    }
    const days = Math.floor(minutes / 1440);
    const restOfDay = minutes % 1440;
    const dayPart = unitLabel(days, 'day', 'days', t);
    // The remainder is the same question one unit down, so it is answered by
    // the same function rather than by a second copy of the rule.
    return restOfDay === 0 ? dayPart : `${dayPart} ${formatDurationLabel(restOfDay, t)}`;
};

/**
 * The length of a booked window, written out — the ONE label for "how long is
 * this booking", shared by the booking-details field, the price box beside it
 * (`NonRoomRentalReservationSummary`) and the visitor-booking search
 * (`BookingInfo`).
 *
 * Elapsed minutes, not calendar days: a non-room window is "start + N
 * minutes", so `diff(start, 'days')` floored a 3-day storage booking to
 * "2 Tage" in the price box while the details field said "3 Tage", and showed
 * nothing at all under a day. Same rule as the duration dropdowns
 * (`formatDurationLabel`), so field, box and options cannot disagree.
 *
 * @returns `''` when either end is missing or unparsable, or the window is not
 *   positive — callers decide what an absent duration reads as
 */
export const formatBookedWindowLabel = (
    start: Date | string | null | undefined,
    end: Date | string | null | undefined,
    t: TFunction,
): string => {
    if (!start || !end) return '';
    const minutes = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60_000);
    if (!Number.isFinite(minutes) || minutes <= 0) return '';
    return formatDurationLabel(minutes, t);
};

// Days carrying at least one bookable duration, as YYYY-MM-DD for the date
// pickers. Both availability shapes (plain count for storage, { count, price }
// for rental/SUP) are read through `getAvailabilityCount`.
export const deriveAvailableStartDates = (
    availability: AssetGroupAvailability | undefined,
): string[] => {
    if (!availability) return [];

    return Object.keys(availability)
        .filter((dateKey) => Object.values(availability[dateKey]).some((durations) =>
            Object.values(durations).some((slot) => getAvailabilityCount(slot) > 0)))
        .map((dateKey) => {
            const [day, month, year] = dateKey.split('.');
            return `${year}-${month}-${day}`;
        });
};

// Bookable start times on the given day.
export const deriveAvailableTimeSlots = (
    availability: AssetGroupAvailability | undefined,
    startDate: Dayjs | null,
): string[] => {
    if (!availability || !startDate) return [];

    const daySlot = availability[startDate.format(AVAILABILITY_DATE_KEY_FORMAT)];
    if (!daySlot) return [];

    return Object.keys(daySlot)
        .filter((timeKey) => Object.values(daySlot[timeKey]).some((slot) => getAvailabilityCount(slot) > 0))
        // Explicit comparator (times are "HH:mm" strings) — a bare .sort()
        // is flagged as unreliable (SonarQube S2871).
        .sort((a, b) => a.localeCompare(b));
};

// Bookable durations for a given day + start time, minus the ones that would
// end on a weekday the tenant refuses hand-backs on (`skipEndingInDays`).
export const deriveAvailableDurationOptions = (params: {
    availability: AssetGroupAvailability | undefined;
    startDate: Dayjs | null;
    startTime: string;
    skipEndingInDays?: readonly string[];
    t: TFunction;
    /** The group's location zone (#3219); the station default when omitted. */
    timeZone?: string;
}): DurationOption[] => {
    const { availability, startDate, startTime, skipEndingInDays, t, timeZone = STATION_TIME_ZONE } = params;
    if (!availability || !startDate || !startTime) return [];

    const options = getAvailableDurations(availability, startDate, startTime)
        .map((minutes) => ({ value: minutes, label: formatDurationLabel(minutes, t) }));

    if (!skipEndingInDays?.length) return options;

    // The slot is the station's wall clock, and so is the weekday the booking
    // ends on — not the browser's.
    const startDateTime = stationDateTime(startDate, startTime, timeZone);

    return options.filter((option) => {
        if (option.value >= TWO_MONTHS_IN_MINUTES) return true;
        const endInstant = startDateTime.add(option.value, 'minutes').toDate();
        const endDayName = DAY_NAMES[atStation(endInstant, timeZone).weekday % 7];
        return !skipEndingInDays.includes(endDayName);
    });
};

// Whether the booking-edit dialog can drive its window off availability, or has
// to fall back to free-form date/time fields.
//
// Availability is not guaranteed to arrive: the request can fail, a group can
// have no usable pricescale rows, or the payload can come back empty for a
// window we can't predict. When it does, every option list collapses to the
// booking's own current values (`withCurrentOption` keeps those selectable) and
// the operator can open the dialog but change nothing — which is exactly what
// happened on production to a SUP booking whose group returned nothing.
//
// So the availability UI is only used when the payload actually offers
// something. While the request is still in flight it is kept, so the form
// doesn't flash the free-form fields and then swap; the decision settles once,
// before the operator can interact.
export const shouldUseAvailabilityWindow = (params: {
    useCaseSupportsIt: boolean;
    isLoading: boolean;
    availableStartDateCount: number;
}): boolean => {
    const { useCaseSupportsIt, isLoading, availableStartDateCount } = params;
    if (!useCaseSupportsIt) return false;
    return isLoading || availableStartDateCount > 0;
};

// Applies a date change to an existing booking window, keeping the time of day
// (on the station's clock) and the duration. Returns null when the change must
// be ignored. `date` is the picker's value; `currentStart` the stored instant.
//
// react-day-picker's single mode *deselects* when the already-selected day is
// clicked again — the natural "yes, this date" gesture — and reports that as
// `null`. A booking always has a window, so treating that as "clear it" wiped
// start, end and duration in one tap, after which the start time fell back to
// 00:00. Ignoring it is the only sane reading here.
export const applyDateToWindow = (
    date: Dayjs | null,
    currentStart: Dayjs | null,
    durationMinutes: number,
    timeZone: string = STATION_TIME_ZONE,
): { start: Dayjs, end: Dayjs | null } | null => {
    if (!date || !currentStart) return null;
    const start = stationDateTime(date, stationTimeOfDay(currentStart, timeZone), timeZone);
    return { start, end: durationMinutes > 0 ? start.add(durationMinutes, 'minute') : null };
};

// Availability describes what is free for a *new* booking, so the window an
// existing booking already occupies can read as unavailable while editing it.
// The booking's own current value is therefore always offered — the backend
// re-checks capacity on save with the order's own item excluded
// (`assertCapacityAvailableTx(..., { excludeOrderId })`), which is the
// authority on whether the chosen window is actually bookable.
export const withCurrentOption = <T>(
    options: T[],
    current: T | null | undefined,
    isSame: (option: T, currentValue: T) => boolean,
    compare: (a: T, b: T) => number,
): T[] => {
    if (current === null || current === undefined) return options;
    if (options.some((option) => isSame(option, current))) return options;
    return [...options, current].sort(compare);
};

// Which use cases pick their booking window from the group's availability
// rather than from free-form date/time fields.
//
// This is the same question the create page answers implicitly: it drives date,
// start time and duration off `useGetAssetGroupAvailabilitiesQuery` for EVERY
// non-room use case, gated only on a selected group
// (`CreateNonRoomBookingPage.tsx:141-147`). The edit dialog is the only surface
// that ever asked the question separately, and answering it differently is how
// the two drifted apart.
//
// Storage was excluded here until 2026-09-16, on the worry that its groups might
// return no usable pricescale rows and leave the dialog unusable. Two things
// retire that worry: `shouldUseAvailabilityWindow` above already falls back to
// the free-form fields whenever the payload offers nothing, and the create page
// has been booking storage off the very same query all along — so a real storage
// availability response is not a hypothetical, it is what every storage booking
// is already made from.
//
// What the exclusion cost: the free-form branch has no duration list at all, so
// an operator could type any start and any end. A KGSt-Forum booking came out of
// it as 15:05-16:45 — an hour and forty minutes, a window no customer could ever
// have selected, outside every tariff the station sells.
const AVAILABILITY_DRIVEN_USE_CASES = new Set(['rental', 'sup', 'storage']);

export const usesAvailabilityWindowForUseCase = (useCase: string | null | undefined): boolean =>
    AVAILABILITY_DRIVEN_USE_CASES.has((useCase ?? '').toLowerCase());
